import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Button,
  Card,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { PageHeader, FilterBar, EmptyState, StatusChip } from '../components/ui';
import { adminAPI } from '../services/api';
import useListQueryParams from '../hooks/useListQueryParams';
import { formatAdminInr } from '../utils/formatAdminPrice.js';
import { formatAdminIstDateTime } from '../utils/adminIstDateTime.js';
import { useAuth } from '../context/AuthContext';
import './Registrations.css';

function paymentLabel(mode) {
  const m = String(mode || '').trim().toLowerCase();
  if (m === 'online' || m === 'payment_gateway' || m === 'razorpay') return 'online';
  if (m === 'cash' || m === 'office') return 'cash';
  return mode || '—';
}

export default function ManualReceipts() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canCreate = can('receipts_manage') || can('receipts');
  const { query, setQuery } = useListQueryParams({ page: '1', search: '' });
  const page = Number(query.page || 1);
  const search = query.search || '';
  const [viewRow, setViewRow] = useState(null);

  const { data, isLoading } = useQuery(
    ['admin-manual-receipts', page, search],
    () => adminAPI.getManualReceipts({ page, limit: 50, search: search.trim() || undefined }),
    { keepPreviousData: true }
  );

  const list = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};

  const totalLabel = useMemo(() => {
    if (pagination.total != null) return `${pagination.total} total`;
    return `${list.length} shown`;
  }, [pagination.total, list.length]);

  return (
    <div className="registrations-page">
      <PageHeader
        title="Manual Receipts"
        subtitle="Standalone receipts (legacy Hasura-style). Receipt ID continues automatically."
        actions={
          canCreate ? (
            <Button component={Link} to="/manual-receipts/new" variant="contained" startIcon={<AddRoundedIcon />}>
              Create Receipt
            </Button>
          ) : null
        }
      />

      <FilterBar contentSx={{ py: 2 }}>
        <TextField
          size="small"
          label="Search"
          placeholder="ID, name, trek, payment notes…"
          value={search}
          onChange={(e) => setQuery({ search: e.target.value, page: '1' })}
          sx={{ minWidth: 280, maxWidth: 420, flex: '1 1 280px' }}
        />
      </FilterBar>

      <Card className="ops-table-card">
        {isLoading ? (
          <div className="ops-loading-box">
            <CircularProgress size={32} sx={{ color: 'primary.main' }} />
            <Typography variant="body2" color="text.secondary">Loading manual receipts…</Typography>
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            title="No manual receipts found"
            description="Try a different search term, or create a new receipt."
            actionLabel={canCreate ? 'Create Receipt' : undefined}
            onAction={canCreate ? () => navigate('/manual-receipts/new') : undefined}
          />
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Id</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Receipt For</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Amount</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Total Participants</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Payment</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Created At</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Created By</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', bgcolor: '#f8fafc' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{row.id}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                    <TableCell>{row.receiptFor}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {formatAdminInr(row.amount)}
                    </TableCell>
                    <TableCell align="center">{row.totalParticipants ?? '—'}</TableCell>
                    <TableCell sx={{ textTransform: 'lowercase' }}>{paymentLabel(row.paymentMode)}</TableCell>
                    <TableCell>
                      <StatusChip status={row.status} label={row.status || '—'} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatAdminIstDateTime(row.createdAt)}</TableCell>
                    <TableCell>{row.createdByName || '—'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => setViewRow(row)} aria-label="View receipt" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}>
                          <VisibilityRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!isLoading && list.length > 0 ? (
          <div className="ops-table-footer">
            <span className="ops-table-footer-info">
              {pagination.totalPages > 1
                ? `Page ${page} of ${pagination.totalPages} · ${totalLabel}`
                : totalLabel}
            </span>
            {pagination.totalPages > 1 ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" disabled={page <= 1} onClick={() => setQuery({ page: String(page - 1) })}>
                  Previous
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setQuery({ page: String(page + 1) })}
                >
                  Next
                </Button>
              </Box>
            ) : null}
          </div>
        ) : null}
      </Card>

      <Dialog open={Boolean(viewRow)} onClose={() => setViewRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Manual receipt #{viewRow?.id}</DialogTitle>
        <DialogContent dividers>
          {viewRow ? (
            <Box sx={{ display: 'grid', gap: 1.25 }}>
              <Typography variant="body2">
                <strong>Name:</strong> {viewRow.name}
              </Typography>
              <Typography variant="body2">
                <strong>Receipt for:</strong> {viewRow.receiptFor}
              </Typography>
              <Typography variant="body2">
                <strong>Amount:</strong> {formatAdminInr(viewRow.amount)}
              </Typography>
              <Typography variant="body2">
                <strong>Total participants:</strong> {viewRow.totalParticipants ?? '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Payment:</strong> {paymentLabel(viewRow.paymentMode)}
              </Typography>
              <Typography variant="body2">
                <strong>Payment ID / notes:</strong> {viewRow.paymentId || '—'}
              </Typography>
              <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <strong>Status:</strong> <StatusChip status={viewRow.status} label={viewRow.status} />
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {formatAdminIstDateTime(viewRow.createdAt)} by {viewRow.createdByName}
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
